import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, Lock, Mail, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChiffreurLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/chiffreur/login`, {
        email,
        password
      });
      
      // Store token
      localStorage.setItem('chiffreur_token', response.data.access_token);
      localStorage.setItem('chiffreur_data', JSON.stringify(response.data.chiffreur));
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      toast.success('Bienvenue !');
      navigate('/chiffreur/dashboard');
      
    } catch (error) {
      console.error('Chiffreur login error:', error);
      if (error.response?.status === 401) {
        toast.error('Email ou mot de passe incorrect');
      } else {
        toast.error('Erreur de connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900">
      {/* Header */}
      <header className="bg-transparent py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Accueil
          </Button>
          <div className="flex-1 text-center">
            <h1 className="text-xl font-bold text-white tracking-wider">L'ŒIL DU MARCHAND</h1>
            <p className="text-xs text-emerald-300">Espace Chiffreur</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md bg-white/5 backdrop-blur-xl border-white/10">
          <CardContent className="pt-8 pb-6 px-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Calculator className="w-8 h-8 text-emerald-400" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Connexion Chiffreur</h2>
              <p className="text-white/60 text-sm">Accédez à vos dossiers de chiffrage</p>
            </div>

            {/* Info Box */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-6">
              <p className="text-emerald-300 text-sm">
                <strong>📋 Votre mission :</strong> Réaliser le chiffrage des travaux pour les dossiers assignés. 
                Les données personnelles des clients sont masquées.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-white/80 text-sm">
                  Adresse email
                </Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="pl-10 bg-white/5 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-white/80 text-sm">
                  Mot de passe
                </Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 bg-white/5 border-white/20 text-white placeholder:text-white/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gold hover:text-gold/80 transition-colors z-10"
                    style={{ cursor: 'pointer' }}
                  >
                    {showPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-5"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Connexion...
                  </div>
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>

            {/* Footer */}
            <p className="text-center text-white/40 text-xs mt-6">
              Compte créé par l'administrateur
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
